import React, { useState, useCallback, useEffect, useRef } from 'react';
import { AlertTriangle, Archive, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { _registerDeleteGuardModal } from '../services/entityClient';

// Localized dependency labels — matches the backend DEPENDENCY_MAP.
const COLLECTION_LABELS = {
  invoices:              'فواتير',
  quotations:            'عروض أسعار',
  projects:              'مشاريع',
  credit_notes:          'إشعارات دائنة',
  payments:              'مدفوعات',
  supplier_bills:        'فواتير مورّد',
  purchase_orders:       'أوامر شراء',
  expenses:              'مصروفات',
  progress_claims:       'مستخلصات',
  variation_orders:      'أوامر تغيير',
  subcontracts:          'عقود باطن',
  subcontract_claims:    'مستخلصات باطن',
  subcontract_payments:  'مدفوعات باطن',
  equipment_usage:       'سجلات معدات',
  fleet_logs:            'سجلات أسطول',
  payroll:               'مسيرات رواتب',
  employee_loans:        'سلف موظفين',
  employee_petty_cash:   'نثرية موظفين',
  field_visits:          'زيارات ميدانية',
};

const ENTITY_LABELS = {
  customer:       'العميل',
  supplier:       'المورّد',
  project:        'المشروع',
  invoice:        'الفاتورة',
  payment:        'الدفعة',
  inventory:      'المنتج',
  employee:       'الموظف',
  cost_center:    'مركز التكلفة',
  subcontractor:  'مقاول الباطن',
  subcontract:    'عقد الباطن',
  quotation:      'عرض السعر',
  credit_note:    'الإشعار الدائن',
};

export default function DeleteGuardProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState(null);
  const [reason, setReason] = useState('');
  const [archiving, setArchiving] = useState(false);
  const resolverRef = useRef(null);

  // Promise-based ask: the entityClient awaits this and gets back the user's decision.
  const ask = useCallback((p) => {
    setPayload(p);
    setReason('');
    setOpen(true);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  // Register ourselves with the entityClient module so any page can call
  // `entityClient.delete(...)` and have its 409 routed here.
  useEffect(() => {
    _registerDeleteGuardModal(ask);
    return () => _registerDeleteGuardModal(null);
  }, [ask]);

  const close = (decision) => {
    setOpen(false);
    setArchiving(false);
    const r = resolverRef.current;
    resolverRef.current = null;
    if (r) r(decision);
  };

  const handleArchive = async () => {
    setArchiving(true);
    try {
      // The entityClient is the one that performs the archive call;
      // here we just resolve with 'archive' + the reason. The wrapper
      // catches network errors and shows a toast itself if needed.
      close({ action: 'archive', reason: reason || null });
      toast.success(`تم أرشفة ${ENTITY_LABELS[payload.entity] || payload.entity}`);
    } catch (err) {
      toast.error('فشل الأرشفة: ' + (err?.message || 'خطأ غير معروف'));
      setArchiving(false);
    }
  };

  const handleCancel = () => close({ action: 'cancel' });

  return (
    <>
      {children}
      {open && payload && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
          data-testid="delete-guard-modal"
          onClick={(e) => { if (e.target === e.currentTarget) handleCancel(); }}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-50 to-rose-50 border-b border-amber-200 px-6 py-4 flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-700" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-bold text-slate-900">
                  لا يمكن حذف {ENTITY_LABELS[payload.entity] || payload.entity}
                </h2>
                {payload.displayName && (
                  <p className="text-sm text-slate-700 mt-0.5 font-medium">
                    &ldquo;{payload.displayName}&rdquo;
                  </p>
                )}
              </div>
              <button
                onClick={handleCancel}
                className="p-1 hover:bg-white/60 rounded text-slate-500 hover:text-slate-700"
                data-testid="delete-guard-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-slate-700">
                هذا السجل مرتبط بسجلات أخرى لا يمكن فصلها. اختر <span className="font-semibold">أرشفة</span> للحفاظ على العلاقات والمراجعة المحاسبية.
              </p>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1">
                  السجلات المرتبطة:
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(payload.dependencies || {}).map(([coll, count]) => (
                    <span
                      key={coll}
                      className="bg-amber-100 text-amber-900 px-2.5 py-1 rounded-md text-xs font-medium"
                      data-testid={`delete-guard-dep-${coll}`}
                    >
                      {COLLECTION_LABELS[coll] || coll}: {count}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  سبب الأرشفة (اختياري)
                </label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="مثلاً: إنهاء التعامل / تم الاستبدال / إلخ"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
                  data-testid="delete-guard-reason"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="bg-slate-50 border-t border-slate-200 px-6 py-3 flex items-center justify-end gap-2">
              <button
                onClick={handleCancel}
                disabled={archiving}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 rounded-lg disabled:opacity-50"
                data-testid="delete-guard-cancel"
              >
                إلغاء
              </button>
              <button
                onClick={handleArchive}
                disabled={archiving}
                className="px-4 py-2 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-lg flex items-center gap-2 disabled:opacity-50"
                data-testid="delete-guard-archive"
              >
                {archiving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Archive className="w-4 h-4" />
                )}
                أرشفة بدلاً من الحذف
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
